import { create } from 'zustand';
import { coachMoveFlagsApi } from '../lib/api';
import type {
  CoachMoveDecideAction, CoachMoveDiscoveryMethod, CoachMoveFlagItem, TrustedRep,
} from '../types';

type FlagView = 'queue' | 'call-list' | 'mine' | 'decided';

interface CoachMoveFlagState {
  flags: CoachMoveFlagItem[];
  trustedReps: TrustedRep[];
  queueCount: number;
  callListCount: number;
  view: FlagView;
  isLoading: boolean;
  isSaving: boolean;
  error: string | null;
  fetch: (view: FlagView) => Promise<void>;
  fetchCounts: () => Promise<void>;
  confirm: (id: string, verifyMethod: CoachMoveDiscoveryMethod) => Promise<void>;
  decide: (id: string, body: { action: CoachMoveDecideAction; toOrganizationId?: string; note?: string }) => Promise<void>;
  revert: (id: string) => Promise<void>;
  fetchTrustedReps: () => Promise<void>;
  setTrusted: (userId: string, trusted: boolean) => Promise<void>;
}

async function refresh(get: () => CoachMoveFlagState) {
  await Promise.all([get().fetch(get().view), get().fetchCounts()]);
}

export const useCoachMoveFlagStore = create<CoachMoveFlagState>((set, get) => ({
  flags: [],
  trustedReps: [],
  queueCount: 0,
  callListCount: 0,
  view: 'queue',
  isLoading: false,
  isSaving: false,
  error: null,

  fetch: async (view) => {
    set({ isLoading: true, error: null, view });
    try {
      const { flags } = await coachMoveFlagsApi.list(view);
      set({ flags, isLoading: false });
    } catch (e) {
      const err = e as { response?: { data?: { message?: string } } };
      set({ isLoading: false, error: err.response?.data?.message || 'Failed to load flags' });
    }
  },
  fetchCounts: async () => {
    try {
      const [queue, callList] = await Promise.all([
        coachMoveFlagsApi.count('queue').catch(() => ({ count: 0 })),
        coachMoveFlagsApi.count('call-list').catch(() => ({ count: 0 })),
      ]);
      set({ queueCount: queue.count, callListCount: callList.count });
    } catch { /* badges are best-effort */ }
  },
  confirm: async (id, verifyMethod) => {
    set({ isSaving: true });
    try { await coachMoveFlagsApi.confirm(id, verifyMethod); await refresh(get); }
    catch (e) {
      const err = e as { response?: { data?: { message?: string } } };
      throw new Error(err.response?.data?.message || 'Failed to confirm');
    }
    finally { set({ isSaving: false }); }
  },
  decide: async (id, body) => {
    set({ isSaving: true });
    try { await coachMoveFlagsApi.decide(id, body); await refresh(get); }
    catch (e) {
      const err = e as { response?: { data?: { message?: string } } };
      throw new Error(err.response?.data?.message || 'Failed to decide');
    }
    finally { set({ isSaving: false }); }
  },
  revert: async (id) => {
    set({ isSaving: true });
    try { await coachMoveFlagsApi.revert(id); await refresh(get); }
    catch (e) {
      const err = e as { response?: { data?: { message?: string } } };
      throw new Error(err.response?.data?.message || 'Failed to revert');
    }
    finally { set({ isSaving: false }); }
  },
  fetchTrustedReps: async () => {
    try {
      const { users } = await coachMoveFlagsApi.trustedReps();
      set({ trustedReps: users });
    } catch { /* section is steward-only; ignore */ }
  },
  setTrusted: async (userId, trusted) => {
    set({ isSaving: true });
    try { await coachMoveFlagsApi.setTrusted(userId, trusted); await get().fetchTrustedReps(); }
    catch (e) {
      const err = e as { response?: { data?: { message?: string } } };
      throw new Error(err.response?.data?.message || 'Failed to update');
    }
    finally { set({ isSaving: false }); }
  },
}));
